/* Button primitive. Zero domain knowledge.

   Wraps a real <button> so keyboard, focus and form semantics come from the
   platform rather than being reimplemented. */

import { LitElement, html, css } from 'lit';
import { shadowReset, touchTarget } from '../../design/base.js';
import { customElement, property } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';

@customElement('g-button')
export class GButton extends LitElement {
  static override styles = [shadowReset, css`
    :host {
      display: inline-flex;
    }
    :host([full]) {
      display: flex;
      flex: 1;
    }
    button {
      display: inline-flex;
      align-items: center;
      gap: var(--space-2);
      width: 100%;
      /* --g-button-pad lets a dense context (e.g. a table-like row) shrink the
         hit area without the caller reaching into this shadow root. */
      padding: var(--g-button-pad, var(--space-2) var(--space-3));
      /* Let a caller that pins the host's height actually get it (the rail, so
         its rows don't change height on collapse). Without this the automatic
         flex minimum — the label's own line box — overflows the host instead.
         Inert everywhere else: nothing else constrains the host. */
      min-height: 0;
      border: 1px solid transparent;
      border-radius: var(--radius-md);
      background: none;
      color: inherit;
      font: inherit;
      font-family: var(--font-ui);
      cursor: pointer;
      text-align: left;
      transition: background-color 120ms ease, color 120ms ease;
    }
    /* Guarded: on touch a hover latches and outlives the tap (see g-chips). */
    @media (hover: hover) {
      button:hover:not(:disabled) {
        background: var(--surface-2);
      }
    }
    button:active:not(:disabled) {
      background: var(--press);
    }
    /* The tinted variants already own their background, so the shared wash would
       be painted over rather than seen. They dim instead. */
    :host([variant='primary']) button:active:not(:disabled),
    :host([selected]) button:active:not(:disabled) {
      filter: brightness(0.92);
    }
    button:disabled {
      opacity: 0.45;
      cursor: default;
    }

    :host([variant='primary']) button {
      background: var(--accent);
      color: var(--accent-ink);
      justify-content: center;
    }
    @media (hover: hover) {
      :host([variant='primary']) button:hover:not(:disabled) {
        filter: brightness(1.06);
      }
    }

    /* Destructive action. Tinted only on hover/focus, so at rest it sits quietly
       alongside its neighbours (§31) and the warning appears exactly when the
       pointer is on it. Lives here rather than in each caller because "delete is
       red" is a property of the action, not of the screen it happens to be on. */
    /* Split rather than wrapped: the keyboard half must keep working on a
       touchscreen, so only the hover half goes behind the guard. */
    :host([variant='danger']) button:focus-visible:not(:disabled) {
      background: color-mix(in oklab, var(--danger) 12%, transparent);
      color: var(--danger);
    }
    @media (hover: hover) {
      :host([variant='danger']) button:hover:not(:disabled) {
        background: color-mix(in oklab, var(--danger) 12%, transparent);
        color: var(--danger);
      }
    }

    /* selected nav item — the rail's active state */
    :host([selected]) button {
      background: color-mix(in oklab, var(--accent) 16%, transparent);
      color: var(--accent);
      font-weight: 700;
    }

    :host([icon-only]) button {
      justify-content: center;
      padding: var(--g-button-pad, var(--space-2));
      width: auto;
      aspect-ratio: 1;
    }
  `,
  /* Last, so its `position: relative` on the inner button can't be overwritten
     by a rule above. Inert on a mouse — it is entirely inside a
     `pointer: coarse` query. */
  touchTarget];

  @property({ reflect: true }) variant: 'ghost' | 'primary' | 'danger' = 'ghost';
  @property({ type: Boolean, reflect: true }) selected = false;
  @property({ type: Boolean, reflect: true }) disabled = false;
  @property({ type: Boolean, reflect: true, attribute: 'icon-only' }) iconOnly = false;
  /** Accessible name — required when the button renders only an icon. */
  @property() label = '';

  override render() {
    // ifDefined drops the attribute entirely rather than emitting an empty
    // aria-label, which screen readers treat as a nameless control.
    const name = this.label || undefined;
    return html`
      <button ?disabled=${this.disabled} aria-label=${ifDefined(name)} title=${ifDefined(name)}>
        <slot></slot>
      </button>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'g-button': GButton;
  }
}
